import { type OptionState, OptionsType } from '@openx/types/options';
import { useMemo } from 'react';
import { useOptionsByPathQuery } from '../../../hooks/useOptionsByPathQuery';
import { toList } from './toList';
import { toMapById } from './toMapById';

type DeviceTypesOptions = {
  [OptionsType.DESKTOP_DEVICES]: OptionState;
  [OptionsType.MOBILE_DEVICES]: OptionState;
  [OptionsType.TV_DEVICES]: OptionState;
};

export const useDeviceTypesOptionsFetch = (): DeviceTypesOptions => {
  const {
    data: { optionsByPath: desktopDevices = [] } = {},
    loading: isDesktopDevicesLoading,
    refetch: desktopDevicesRefetch,
  } = useOptionsByPathQuery({
    variables: {
      path: 'deal.package.targeting.rendering_context.device_type.desktop_devices',
    },
  });
  const {
    data: { optionsByPath: mobileDevices = [] } = {},
    loading: isMobileDevicesLoading,
    refetch: mobileDevicesRefetch,
  } = useOptionsByPathQuery({
    variables: {
      path: 'deal.package.targeting.rendering_context.device_type.mobile_devices',
    },
  });
  const {
    data: { optionsByPath: tvDevices = [] } = {},
    loading: isTVDevicesLoading,
    refetch: tvDevicesRefetch,
  } = useOptionsByPathQuery({
    variables: {
      path: 'deal.package.targeting.rendering_context.device_type.tv_devices',
    },
  });

  const desktopDevicesList = useMemo(() => toList(desktopDevices), [desktopDevices]);
  const mobileDevicesList = useMemo(() => toList(mobileDevices), [mobileDevices]);
  const tvDevicesList = useMemo(() => toList(tvDevices), [tvDevices]);

  const desktopDevicesMapById = useMemo(() => toMapById(desktopDevicesList), [desktopDevicesList]);
  const mobileDevicesMapById = useMemo(() => toMapById(mobileDevicesList), [mobileDevicesList]);
  const tvDevicesMapById = useMemo(() => toMapById(tvDevicesList), [tvDevicesList]);

  return useMemo(
    () => ({
      [OptionsType.DESKTOP_DEVICES]: {
        loading: isDesktopDevicesLoading,
        list: desktopDevicesList,
        mapById: desktopDevicesMapById,
        refetch: desktopDevicesRefetch,
      },
      [OptionsType.MOBILE_DEVICES]: {
        loading: isMobileDevicesLoading,
        list: mobileDevicesList,
        mapById: mobileDevicesMapById,
        refetch: mobileDevicesRefetch,
      },
      [OptionsType.TV_DEVICES]: {
        loading: isTVDevicesLoading,
        list: tvDevicesList,
        mapById: tvDevicesMapById,
        refetch: tvDevicesRefetch,
      },
    }),
    [
      desktopDevicesRefetch,
      isDesktopDevicesLoading,
      isMobileDevicesLoading,
      isTVDevicesLoading,
      mobileDevicesRefetch,
      tvDevicesRefetch,
      desktopDevicesList,
      desktopDevicesMapById,
      mobileDevicesList,
      mobileDevicesMapById,
      tvDevicesList,
      tvDevicesMapById,
    ]
  );
};
